import {
  addDays,
  CHECKIN_FUTURE_CONFIRM_MS,
  checkinDayOccurrences,
  epochOfLocalDateTime,
  FADE_LOOKBACK_MAX_DAYS,
  formatMin,
  liveOnly,
  OccurrenceReason,
  personalDateOf,
  type CheckinLogRecord,
  type CheckinOccurrence,
  type LiveCheckinItem,
} from '@wiztime/core';
import { queries } from '@wiztime/storage';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { settingsPath, SettingsSection } from '../../../app/routes';
import { useLive } from '../../../hooks/useLive';
import { useRunCommand } from '../../../hooks/useRunCommand';
import { useServices, useStore } from '../../../hooks/useServices';
import { AddPopover } from '../../../ui/AddPopover';
import { ConfirmDialog } from '../../../ui/ConfirmDialog';
import { Popover } from '../../../ui/Popover';
import type { AnchorRect } from '../dayUiStore';
import { cx } from '../../../lib/cx';
import { Card } from '../../../ui/Card';
import { CheckIcon, PencilIcon } from '../../../ui/icons';
import { useDayData } from '../DayDataContext';
import styles from './Cards.module.css';
import { CheckinAddForm } from './CheckinAddForm';
import { CheckinEditMode } from './CheckinEditMode';
import { createdAtOfUuidV7 } from '../../../lib/uuidTime';
import { doneIndex, isFaded } from './checkinFade';
import { DoneTimeEditor } from './DoneTimeEditor';

const EMPTY_KEYS: ReadonlySet<string> = new Set();

/** A tick on today later than the planned time by more than this shows the "change time" line (§3.2). */
export const LATE_TICK_PROMPT_MIN = 30;
/** How long the "change time" line stays. */
export const LATE_TICK_PROMPT_MS = 8000;
/**
 * Today's card also lists the previous personal day's timed occurrences that are not done and
 * fell due within this long (checkin-occurrence-plan §4: a 03:00 dose missed at 05:00).
 */
export const CHECKIN_OVERDUE_CARRY_MS = 12 * 60 * 60 * 1000;

export type Occurrence = CheckinOccurrence<LiveCheckinItem>;

/** "HH:mm" of an ISO instant in the device's local time zone (the actual tick time, §8.1). */
function localClock(iso: string): string | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return formatMin(date.getHours() * 60 + date.getMinutes());
}

/** Log data with the envelope HLC: of a legacy and a new record of one occurrence the newer wins. */
export function dayLogsOf(records: readonly CheckinLogRecord[] | undefined) {
  return (records ?? []).flatMap((log) => (log.data === null ? [] : [{ ...log.data, hlc: log.hlc }]));
}

/**
 * Today's check-in list (2026-10b §8, data dictionary §3.2): one row per occurrence, sorted by
 * planned time — an item whose planned time moved across the day start has two rows on the
 * transition day. Today's card also lists yesterday's overdue doses on top. Long-unticked items
 * fold under "Faded". The title row has "+" (the shared add popover) and "Edit" (the edit mode
 * for the whole day, plan §3.5); item settings live in Settings → Check-in only.
 */
export function CheckinCard() {
  const { t } = useTranslation();
  const store = useStore();
  // The device clock, read when the user acts (the day view's `now` lags by up to 30 s).
  const { now: clock } = useServices();
  const run = useRunCommand();
  const { date, settings, now } = useDayData();
  const today = personalDateOf(now, settings.dayStartMin);
  const isToday = date === today;
  // The completion-time editor, anchored to the time button of one row.
  const [editing, setEditing] = useState<{ readonly key: string; readonly anchor: AnchorRect } | null>(null);
  // Edit mode belongs to the day it was opened on: switching days leaves it.
  const [editDate, setEditDate] = useState<string | null>(null);
  const editMode = editDate === date;
  const setEditMode = (on: boolean): void => setEditDate(on ? date : null);
  // The "recorded as done at … · change time" line of a late tick.
  const [late, setLate] = useState<{ readonly key: string; readonly date: string } | null>(null);
  // A tick of a future occurrence waiting for confirmation.
  const [confirmFuture, setConfirmFuture] = useState<Occurrence | null>(null);
  useEffect(() => {
    if (late === null) return;
    const id = window.setTimeout(() => setLate(null), LATE_TICK_PROMPT_MS);
    return () => window.clearTimeout(id);
  }, [late]);

  const items = useLive(() => queries.checkinItems(store.repo), [store]);
  // The occurrences of the day and of the day before (overdue doses).
  // Legacy logs of D-1 and D can be stored on D-2 … D+2 (checkinDayOccurrences reads that range).
  const dayLogs = useLive(() => queries.recentCheckinLogs(store.repo, addDays(date, 1), 3), [store, date]);
  const recent = useLive(
    // One range query for the whole fade lookback (requirements §5.3), not one per item or day.
    () => queries.recentCheckinLogs(store.repo, addDays(date, -1), FADE_LOOKBACK_MAX_DAYS),
    [store, date],
  );

  // Rows unticked in this page session stay on the card until the viewed day changes, so the row
  // does not vanish under the pointer (requirements §5.3). Keyed by day: a day switch drops it.
  const [kept, setKept] = useState<{ readonly date: string; readonly keys: ReadonlySet<string> }>({
    date,
    keys: new Set(),
  });
  if (kept.date !== date) setKept({ date, keys: EMPTY_KEYS }); // left the day: forget, even if we come back
  const keptKeys = kept.date === date ? kept.keys : EMPTY_KEYS;
  if (editMode && editing !== null) setEditing(null);

  const { all, active, shown, faded, overdue, byKey } = useMemo(() => {
    const live = liveOnly(items.data ?? []);
    const logs = dayLogsOf(dayLogs.data);
    const legacyStart = settings.checkinLegacyDayStartMin;
    // Display set (data dictionary §3.2): occurrences due on the day, occurrences done on it
    // (disabled items included), plus the ones unticked during this page session.
    const day = checkinDayOccurrences(live, logs, date, settings.dayStartMin, legacyStart, keptKeys);
    const index = doneIndex(
      [...(dayLogs.data ?? []), ...(recent.data ?? [])],
      live,
      settings.dayStartMin,
      legacyStart,
    );
    const fadedList = day.filter(
      (occurrence) =>
        occurrence.reason === OccurrenceReason.Scheduled &&
        isFaded(occurrence.item, date, index, settings.dayStartMin),
    );
    const previous = isToday
      ? checkinDayOccurrences(
          live,
          logs,
          addDays(date, -1),
          settings.dayStartMin,
          legacyStart,
          keptKeys,
        ).filter((occurrence) => {
          if (occurrence.reason !== OccurrenceReason.Scheduled || occurrence.occursAt === null) return false;
          const due = epochOfLocalDateTime(occurrence.occursAt);
          // Not before the item existed (an item added this morning has no overdue dose).
          const createdAt = createdAtOfUuidV7(occurrence.itemId);
          if (createdAt !== null && due < createdAt) return false;
          // Unticked rows stay during the session; done ones leave on the next day switch.
          return (
            due <= now &&
            now - due <= CHECKIN_OVERDUE_CARRY_MS &&
            (!occurrence.done || keptKeys.has(occurrence.key))
          );
        })
      : [];
    return {
      all: live,
      active: live.filter((item) => item.data.active),
      shown: day.filter((occurrence) => !fadedList.includes(occurrence)),
      faded: fadedList,
      overdue: previous,
      byKey: new Map([...previous, ...day].map((occurrence) => [occurrence.key, occurrence])),
    };
  }, [items.data, dayLogs.data, recent.data, date, settings, keptKeys, isToday, now]);

  // A tick shows at once and is dropped when the stored state catches up (or the write fails),
  // so the box does not flicker back to its old state while the transaction commits.
  const [optimistic, setOptimistic] = useState<ReadonlyMap<string, boolean>>(new Map());
  const settled = [...optimistic].filter(([key, done]) => byKey.get(key)?.done === done);
  if (settled.length > 0 && !(dayLogs.loading || dayLogs.data === undefined)) {
    setOptimistic(new Map([...optimistic].filter(([key, done]) => byKey.get(key)?.done !== done)));
  }

  const loading = items.loading || dayLogs.loading || recent.loading;
  const failed = [items, dayLogs, recent].find((query) => query.error !== null);
  const retry = (): void => [items, dayLogs, recent].forEach((query) => query.retry());

  /** A tick of an occurrence planned more than 12 hours ahead, or on a later personal day, asks first (plan §4). */
  const isFuture = (occurrence: Occurrence): boolean => {
    // The exact time of the click, not the day view's clock (refreshed every 30 s).
    const clickedAt = clock();
    return (
      date > personalDateOf(clickedAt, settings.dayStartMin) ||
      (occurrence.occursAt !== null &&
        epochOfLocalDateTime(occurrence.occursAt) - clickedAt > CHECKIN_FUTURE_CONFIRM_MS)
    );
  };

  const write = (occurrence: Occurrence, next: boolean): void => {
    const { key } = occurrence;
    // An overdue dose ticked on today's card was taken now, not at its planned time.
    const carried = overdue.includes(occurrence);
    // Unticked rows, and overdue rows touched at all, stay until the day changes (no row vanishes under the pointer).
    if (!next || carried) {
      setKept((current) => ({
        date,
        keys: new Set([...(current.date === date ? current.keys : EMPTY_KEYS), key]),
      }));
    }
    setOptimistic((current) => new Map(current).set(key, next));
    void run(() =>
      store.toggleCheckin(occurrence.itemId, occurrence.occurrence, next && carried ? Date.now() : undefined),
    ).then((result) => {
      if (result.ok) {
        const lateBy =
          occurrence.occursAt === null ? 0 : Date.now() - epochOfLocalDateTime(occurrence.occursAt);
        if (next && (isToday || carried) && lateBy > LATE_TICK_PROMPT_MIN * 60_000) setLate({ key, date });
      } else {
        setOptimistic((current) => {
          const rest = new Map(current);
          rest.delete(key);
          return rest;
        });
      }
    });
  };

  const toggle = (occurrence: Occurrence): void => {
    const next = !(optimistic.get(occurrence.key) ?? occurrence.done);
    if (next && isFuture(occurrence)) {
      setConfirmFuture(occurrence);
      return;
    }
    write(occurrence, next);
  };

  /** Rows of one list; a thin rule separates the timed items (first) from the untimed ones. */
  const rows = (list: readonly Occurrence[]) =>
    list.flatMap((occurrence, index) => {
      const startsUntimed =
        occurrence.plannedMin === null && index > 0 && list[index - 1]!.plannedMin !== null;
      return startsUntimed
        ? [
            <li
              key={`${occurrence.key}-divider`}
              className={styles.timedDivider}
              role="separator"
              data-testid="checkin-divider"
            />,
            row(occurrence),
          ]
        : [row(occurrence)];
    });

  const openEditor = (key: string, target: HTMLElement): void => {
    const { left, top, right, bottom } = target.getBoundingClientRect();
    setEditing({ key, anchor: { left, top, right, bottom } });
    setLate(null);
  };

  const row = (occurrence: Occurrence) => {
    const { item, plannedMin: planned, key } = occurrence;
    const done = optimistic.get(key) ?? occurrence.done;
    // The stored completion time shows once the stored state agrees with the box.
    const settledDone = done && occurrence.done;
    const tickTime = settledDone && occurrence.doneAt !== null ? localClock(occurrence.doneAt) : null;
    const plannedText = planned === null ? null : formatMin(planned);
    const label =
      item.data.name +
      (plannedText === null ? '' : t('checkin.ariaPlanned', { time: plannedText })) +
      (tickTime !== null
        ? t('checkin.ariaDone', { time: tickTime })
        : settledDone
          ? t('checkin.doneNoTime')
          : '');
    const showLate = late !== null && late.key === key && late.date === date && tickTime !== null;
    return (
      <li key={key}>
        <label
          className={cx(styles.checkRow, done && styles.checkRowDone)}
          data-testid="checkin-row"
          data-item={item.data.name}
          data-occurrence={occurrence.occurrence}
          data-done={done}
        >
          <input type="checkbox" checked={done} aria-label={label} onChange={() => toggle(occurrence)} />
          {plannedText === null ? null : (
            <span className={styles.planCol} data-testid="checkin-planned">
              {plannedText}
            </span>
          )}
          <span className={styles.checkName} title={item.data.name}>
            {item.data.name}
          </span>
          {settledDone ? (
            <button
              type="button"
              className={cx(
                styles.tickTime,
                styles.tickTimeButton,
                tickTime === null && styles.tickTimeEmpty,
              )}
              aria-label={t('checkin.doneTimeButton', { name: item.data.name })}
              aria-haspopup="dialog"
              data-tip={t('checkin.doneTimeTip')}
              data-testid={tickTime === null ? 'checkin-record-time' : 'checkin-tick-time'}
              onClick={(event) => {
                // Inside the row's label: do not let the click also toggle the box.
                event.preventDefault();
                openEditor(key, event.currentTarget);
              }}
            >
              {tickTime === null ? (
                t('checkin.recordTime')
              ) : (
                <>
                  <CheckIcon />
                  {tickTime}
                </>
              )}
            </button>
          ) : null}
        </label>
        {showLate ? (
          <p className={styles.lateLine} role="status" data-testid="checkin-late">
            {t('checkin.lateRecorded', { time: tickTime })}
            {' · '}
            <button
              type="button"
              className={styles.lateLink}
              data-testid="checkin-late-change"
              onClick={(event) => openEditor(key, event.currentTarget)}
            >
              {t('checkin.changeTime')}
            </button>
          </p>
        ) : null}
      </li>
    );
  };

  const editingOccurrence = editing === null ? undefined : byKey.get(editing.key);
  const editingDate =
    editingOccurrence !== undefined && overdue.includes(editingOccurrence) ? addDays(date, -1) : date;
  const editor =
    editing === null || editingOccurrence === undefined || !editingOccurrence.done ? null : (
      <Popover anchor={editing.anchor} label={t('checkin.doneTimeTitle')} onClose={() => setEditing(null)}>
        <DoneTimeEditor
          itemName={editingOccurrence.name}
          date={editingDate}
          dayStartMin={settings.dayStartMin}
          plannedMin={editingOccurrence.plannedMin}
          occursAt={editingOccurrence.occursAt}
          doneAt={editingOccurrence.doneAt}
          // The day view's clock (refreshed every 30 s); storage checks against the exact time.
          nowMs={now}
          onCancel={() => setEditing(null)}
          onSave={(doneAt) => {
            setEditing(null);
            void run(() =>
              store.setCheckinDoneAt(editingOccurrence.itemId, editingOccurrence.occurrence, doneAt),
            );
          }}
        />
      </Popover>
    );

  const manageLink = (
    <Link className={styles.nowrap} to={settingsPath(SettingsSection.Checkin)}>
      {t('checkin.manage')}
    </Link>
  );

  const hasRows = shown.length > 0 || faded.length > 0 || overdue.length > 0;
  const extra =
    loading || failed !== undefined ? undefined : (
      <span className={styles.cardActions}>
        {hasRows && !editMode ? (
          <button
            type="button"
            className={styles.cardIconButton}
            aria-label={t(isToday ? 'checkin.editDay' : 'checkin.editOtherDay')}
            data-tip={t(isToday ? 'checkin.editDay' : 'checkin.editOtherDay')}
            data-testid="checkin-edit"
            onClick={() => setEditMode(true)}
          >
            <PencilIcon />
          </button>
        ) : null}
        <AddPopover label={t('checkin.add')} testId="checkin-add">
          {(close) => <CheckinAddForm names={all.map((item) => item.data.name)} onDone={close} />}
        </AddPopover>
      </span>
    );

  return (
    <Card
      title={t('checkin.title')}
      extra={extra}
      loading={loading}
      error={failed !== undefined}
      errorText={t('checkin.loadError')}
      onRetry={retry}
      testId="checkin-card"
    >
      {editMode ? (
        <CheckinEditMode
          date={date}
          dayStartMin={settings.dayStartMin}
          nowMs={now}
          sections={[
            { key: 'overdue', title: t('checkin.overdueTitle'), date: addDays(date, -1), rows: overdue },
            { key: 'day', title: null, date, rows: [...shown, ...faded] },
          ]}
          onDone={() => setEditMode(false)}
          manageLink={manageLink}
        />
      ) : /* Disabled items done or occurring on an earlier day still show there (§3.3). */
      active.length === 0 && !hasRows ? (
        <div className={styles.empty} data-testid="checkin-empty">
          <p>{t('checkin.empty')}</p>
          <p>
            {t('checkin.emptyHint')} · {manageLink}
          </p>
        </div>
      ) : !hasRows ? (
        <div className={styles.empty} data-testid="checkin-none-today">
          <p>{t('checkin.noneToday')}</p>
          <p>{manageLink}</p>
        </div>
      ) : (
        <>
          {overdue.length > 0 ? (
            <section className={styles.overdue} data-testid="checkin-overdue">
              <h3 className={styles.fadedTitle}>{t('checkin.overdueTitle')}</h3>
              <ul className={styles.list}>{rows(overdue)}</ul>
            </section>
          ) : null}
          <ul className={styles.list}>{rows(shown)}</ul>
          {faded.length > 0 ? (
            <section className={styles.faded} data-testid="checkin-faded">
              <h3 className={styles.fadedTitle}>{t('checkin.faded', { count: faded.length })}</h3>
              <p className={styles.mutedSmall}>{t('checkin.fadedHint')}</p>
              <ul className={styles.list}>{rows(faded)}</ul>
            </section>
          ) : null}
          <p className={styles.mutedSmall}>
            {t('checkin.hint')} {manageLink}
          </p>
          {editor}
        </>
      )}
      {confirmFuture === null ? null : (
        <ConfirmDialog
          title={t('checkin.futureTitle')}
          confirmLabel={t('checkin.futureConfirm')}
          testId="checkin-future-confirm"
          onCancel={() => setConfirmFuture(null)}
          onConfirm={() => {
            const occurrence = confirmFuture;
            setConfirmFuture(null);
            write(occurrence, true);
          }}
        >
          {t('checkin.futureBody', {
            name: confirmFuture.name,
            when:
              confirmFuture.occursAt === null
                ? date.slice(5)
                : `${confirmFuture.occursAt.slice(5, 10)} ${confirmFuture.occursAt.slice(11)}`,
          })}
        </ConfirmDialog>
      )}
    </Card>
  );
}
